import React from 'react';
import { X } from 'lucide-react';

interface ArticleFormProps {
  article: {
    title: string;
    slug: string;
    content: string;
    excerpt: string;
    image_url: string;
    published: boolean;
  };
  onChange: (field: string, value: any) => void;
  onSubmit: (e: React.FormEvent) => void;
  onCancel: () => void;
  isSubmitting: boolean;
}

export function ArticleForm({ article, onChange, onSubmit, onCancel, isSubmitting }: ArticleFormProps) {
  // Auto-generate slug from title
  const handleTitleChange = (value: string) => {
    onChange('title', value);
    // Generate slug from title
    const slug = value
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
    onChange('slug', slug);
  };

  return (
    <form onSubmit={onSubmit} className="space-y-4">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold text-gray-900">
          {article.title ? 'Edit Article' : 'Add Article'}
        </h3>
        <button
          type="button"
          onClick={onCancel}
          className="p-2 text-gray-400 hover:text-gray-500"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700">
          Title
        </label>
        <input
          type="text"
          value={article.title}
          onChange={(e) => handleTitleChange(e.target.value)}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          required
          placeholder="e.g., How to Fix a Leaky Faucet"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700">
          Slug
        </label>
        <input
          type="text"
          value={article.slug}
          onChange={(e) => onChange('slug', e.target.value)}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          required
          placeholder="e.g., how-to-fix-leaky-faucet"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700">
          Excerpt
        </label>
        <textarea
          value={article.excerpt}
          onChange={(e) => onChange('excerpt', e.target.value)}
          rows={2}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          required
          placeholder="Brief summary of the article (appears in previews)"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700">
          Content
        </label>
        <textarea
          value={article.content}
          onChange={(e) => onChange('content', e.target.value)}
          rows={15}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 font-mono"
          required
          placeholder="Article content in Markdown format..."
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700">
          Featured Image URL
        </label>
        <input
          type="url"
          value={article.image_url}
          onChange={(e) => onChange('image_url', e.target.value)}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          placeholder="https://images.unsplash.com/..."
        />
        {article.image_url && (
          <img 
            src={article.image_url} 
            alt="Preview" 
            className="mt-2 h-32 w-full object-cover rounded-lg"
          />
        )}
      </div>

      <div className="flex items-center">
        <input
          type="checkbox"
          id="published"
          checked={article.published}
          onChange={(e) => onChange('published', e.target.checked)}
          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
        />
        <label htmlFor="published" className="ml-2 block text-sm text-gray-900">
          Publish immediately
        </label>
      </div>

      <div className="flex justify-end space-x-3">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isSubmitting}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
        >
          {isSubmitting ? 'Saving...' : 'Save'}
        </button>
      </div>
    </form>
  );
}